import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import styled from 'styled-components';
import { space, fontSize, fontWeight } from 'styled-system';
import { FiArrowUpCircle } from 'react-icons/fi';
import theme from 'styled-theming';
import botTypes from '../bot-types';
import { BotType } from '.';

const DRAG_CONTAINER_HEIGHT = '170px';

const FONT_SIZE = 1;

const backgroundColor = theme('mode', {
  light: (props) => props.theme.colors.white,
  dark: (props) => props.theme.colors.lpGhost,
});

const borderColor = theme('mode', {
  light: (props) => props.theme.colors.superLightGrey,
  dark: 'transparent',
});

// TODO: Break this component down into smaller chunks

const textareaBackgroundColor = theme('mode', {
  light: (props) => props.theme.colors.white,
  dark: (props) => props.theme.colors.lpGhost,
});

const textColor = theme('mode', {
  light: (props) => props.theme.colors.black,
  dark: (props) => props.theme.colors.white,
});

const rangeBackgroundColor = theme('mode', {
  light: (props) => props.theme.colors.greyBlue,
  dark: (props) => props.theme.colors.reallyLightPurple,
});

const rangeIndicatorColor = theme('mode', {
  light: (props) => props.theme.colors.midGrey,
  dark: (props) => props.theme.colors.ghost,
});

const rangeTextColor = theme('mode', {
  light: (props) => props.theme.colors.lightGrey,
  dark: (props) => props.theme.colors.white,
});

const rangeStartColor = theme('mode', {
  light: (props) => props.theme.colors.white,
  dark: (props) => props.theme.colors.lpGhost,
});

const rangeEndColor = theme('mode', {
  light: (props) => props.theme.colors.success,
  dark: (props) => props.theme.colors.richBlue,
});

const placeholderColor = theme('mode', {
  light: (props) => props.theme.colors.midLightGrey,
  dark: (props) => props.theme.colors.midLightGrey,
});

const FileInput = styled.input`
  ${space}
  ${fontSize}
  color: ${textColor};
  font-family: inherit;
`;

const TextArea = styled.textarea`
  ${fontSize}
  ${fontWeight}
  ${space}
  flex: 1;
  border: 1px solid ${borderColor};
  background-color: ${(props) => (props.hasValue ? textareaBackgroundColor : 'transparent')};
  color: ${textColor};
  width: 100%;
  height: ${DRAG_CONTAINER_HEIGHT};
  position: ${(props) => (props.hasValue ? 'relative' : 'absolute')};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
`;

const TextAreaWrapper = styled(Box)`
  position: relative;
`;

const DragContainer = styled(Flex)`
  height: ${DRAG_CONTAINER_HEIGHT};
  background-color: ${(props) => (props.hasValue ? 'transparent' : textareaBackgroundColor)};
`;

const RangeRecomendationIndicator = styled(Box)`
  width: 50%;
  display: inline-block;
  border-width: 0px 1px 1px 1px;
  border-style: solid;
  border-color: ${rangeIndicatorColor};
  height: 10px;
`;

const RangeRecomendation = styled(Box)`
  width: 50%;
  text-align: center;
  border-radius: 3px;
  color: ${rangeTextColor};
  font-size: 12px;
  font-weight: 300;
  display: inline-block;
`;

const StyledRange = styled.input`
  -webkit-appearance: none;
  background-color: transparent;
  font-family: inherit;
  padding: 0;

  /* WEBKIT */

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 5px;
    background: -webkit-linear-gradient(left, ${rangeStartColor} 46%, ${rangeEndColor} 64%);
    border: none;
    border-radius: 3px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    border: none;
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: ${rangeBackgroundColor};
    margin-top: -4px;
  }

  &:focus {
    outline: none;
  }

  &:focus::-webkit-slider-runnable-track {
    background: -webkit-linear-gradient(left, ${rangeStartColor} 46%, ${rangeEndColor} 64%);
  }

  /* MOZILLA */

  /* fix for FF unable to apply focus style bug  */
  border: 1px solid transparent;

  /*required for proper track sizing in FF*/
  width: 100%;

  &::-moz-range-track {
    width: 100%;
    height: 5px;
    background: -moz-linear-gradient(left, ${rangeStartColor} 46%, ${rangeEndColor} 64%);
    background: linear-gradient(to right, ${rangeStartColor} 46%, ${rangeEndColor} 64%);
    border: none;
    border-radius: 3px;
  }

  &::-moz-range-thumb {
    border: none;
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: ${rangeBackgroundColor};
  }

  /*hide the outline behind the border*/
  &:-moz-focusring {
    outline: 1px solid transparent;
    outline-offset: -1px;
  }

  &:focus::-moz-range-track {
    background: -moz-linear-gradient(left, ${rangeStartColor} 46%, ${rangeEndColor} 64%);
    background: linear-gradient(to right, ${rangeStartColor} 46%, ${rangeEndColor} 64%);
  }

  /* IE */

  &::-ms-track {
    width: 100%;
    height: 5px;

    /*remove bg colour from the track, we'll use ms-fill-lower and ms-fill-upper instead */
    background: transparent;

    /*leave room for the larger thumb to overflow with a transparent border */
    border-color: transparent;
    border-width: 6px 0;

    /*remove default tick marks*/
    color: transparent;
  }
  &::-ms-fill-lower {
    background: #777;
    border-radius: 10px;
  }
  &::-ms-fill-upper {
    background: ${rangeStartColor};
    background: linear-gradient(to right, ${rangeStartColor} 46%, ${rangeEndColor} 64%);
    border-radius: 10px;
  }
  &::-ms-thumb {
    border: none;
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #506585;
  }
  &:focus::-ms-fill-lower {
    background: #888;
  }
  &:focus::-ms-fill-upper {
    background: #ccc;
  }
`;

const StyledInput = styled.input`
  ${space}
  ${fontSize}
  ${fontWeight}
  flex: 1;
  border-radius: ${(props) => props.theme.radius.small};
  line-height: ${(props) => props.theme.lineHeights.input};
  border: 1px solid ${borderColor};
  background-color: ${(props) => (props.disabled ? 'transparent' : backgroundColor)};
  color: ${textColor};
  /* Webkit autofill overrides */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    box-shadow: 0 0 1000px ${(props) => props.theme.colors.reallyLightBlue} inset;
    border: 1px solid ${(props) => props.theme.colors.reallyLightPurple};
  }
  font-family: inherit;
  &::placeholder {
    color: ${placeholderColor};
  }
  width: 100%;
`;

const ArrowDownIcon = styled(FiArrowUpCircle)`
  stroke-width: 1.5px;
`;

function Input(props) {
  const {
    inputType,
    value,
    handleChange,
    handleDragOver,
    handleDrop,
    handleFileUpload,
    min,
    name,
    disabled,
    required,
    inputValueFontWeight,
    inputValueFontSize,
    placeHolder,
    ...otherProps
  } = props;

  const hasValue = value !== '';

  if (inputType === 'dragAndDropField') {
    return (
      <Box flex={1}>
        <TextAreaWrapper flex={1}>
          <TextArea
            data-testid="drag-textarea-field"
            px={2}
            py={1}
            fontWeight={inputValueFontWeight || 100}
            fontSize={inputValueFontSize || FONT_SIZE}
            borderRadius={3}
            rows="8"
            value={hasValue ? JSON.stringify(value) : value}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            readOnly
            disabled
            hasValue={hasValue}
          />
          {!hasValue && (
            <DragContainer
              data-testid="drag-container"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              hasValue={hasValue}
            >
              <Box color="#888888" fontSize={2} mb={2}>
                Drop credentials here
              </Box>
              <ArrowDownIcon size={50} color="#afafaf" />
            </DragContainer>
          )}

        </TextAreaWrapper>
        <FileInput
          data-testid="drag-file-input"
          id="fileInput"
          mt={3}
          type="file"
          accept="*.json"
          onChange={handleFileUpload}
          readOnly={!handleFileUpload}
          fontSize={FONT_SIZE}
        />
      </Box>
    );
  }

  if (inputType === 'fileUpload') {
    return (
      <Box flex={1}>
        <FileInput
          data-testid="file-input"
          id="fileInput"
          mt={3}
          type="file"
          accept="*.json"
          onChange={handleFileUpload}
          readOnly={!handleFileUpload}
          fontSize={FONT_SIZE}
        />
      </Box>
    );
  }

  if (inputType === 'radioField') {
    return (
      <Flex data-testid="radio-field" flexWrap="wrap" justifyContent="flex-end">
        {botTypes.map((bt) => (
          <BotType
            key={bt.name}
            type={bt}
            activeBotType={value}
            handleChange={handleChange}
            mr={4}
          />
        ))}
      </Flex>
    );
  }

  if (inputType === 'inputRange') {
    return (
      <Flex
        my={2}
        flex={1}
        flexDirection="column"
        alignItems="flex-end"
        style={{ textAlign: 'right' }}
        data-testid="input-range"
      >
        <StyledRange
          data-testid="range-field"
          type="range"
          min={min}
          value={value}
          onChange={handleChange}
          readOnly={!handleChange}
          name={name}
          disabled={disabled}
          required={required}
          {...otherProps}
        />
        <RangeRecomendationIndicator mt={1} mx={1} />
        <RangeRecomendation mt={2}>Recommended</RangeRecomendation>
      </Flex>
    );
  }

  if (inputType === 'checkbox') {
    return (
      <Flex>
        <input
          data-testid="checkbox-field"
          type="checkbox"
          checked={value}
          onChange={handleChange}
          readOnly={!handleChange}
        />
      </Flex>
    );
  }

  return (
    <StyledInput
      data-testid="text-field"
      p={2}
      fontSize={inputValueFontSize || FONT_SIZE}
      fontWeight={inputValueFontWeight || 300}
      onChange={handleChange}
      readOnly={!handleChange}
      value={value || ''}
      name={name}
      disabled={disabled}
      required={required}
      placeholder={placeHolder || ''}
    />
  );
}

Input.defaultProps = {
  disabled: undefined,
  handleDragOver: undefined,
  handleDrop: undefined,
  handleFileUpload: undefined,
  handleChange: undefined,
  name: undefined,
  value: undefined,
  required: undefined,
  inputType: undefined,
  activeBotType: undefined,
  fileReader: undefined,
  max: undefined,
  min: undefined,
  hint: undefined,
  label: undefined,
  inputValueFontWeight: undefined,
  inputValueFontSize: undefined,
  placeHolder: undefined,
};

Input.propTypes = {
  disabled: PropTypes.bool,
  fileReader: PropTypes.object,
  activeBotType: PropTypes.shape({
    name: PropTypes.string.isRequired,
    inputType: PropTypes.string.isRequired,
    logo: PropTypes.string,
  }),
  handleDragOver: PropTypes.func,
  handleDrop: PropTypes.func,
  handleChange: PropTypes.func,
  handleFileUpload: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  required: PropTypes.bool,
  inputType: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  hint: PropTypes.string,
  label: PropTypes.string,
  inputValueFontWeight: PropTypes.number,
  inputValueFontSize: PropTypes.number,
  placeHolder: PropTypes.string,
};

export default Input;
